import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronLeft, CheckCircle, Clock, Calendar, ChevronDown, ChevronUp, Home, LogOut, Camera } from 'lucide-react';
import API_URL from '../../config';
import '../Home.css';
import './FacultyHome.css';

const FacultyAttendance = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [facultyData, setFacultyData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [activeTab, setActiveTab] = useState('my_periods');

    const [myPeriods, setMyPeriods] = useState([]);
    const [coordinatorRequests, setCoordinatorRequests] = useState([]);
    const [hodRequests, setHodRequests] = useState([]);

    // For My Periods accordion + bulk select
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedIds, setSelectedIds] = useState([]);

    // Sub-tab and history state
    const [subTab, setSubTab] = useState('pending'); // 'pending' | 'history'
    const [historyDate, setHistoryDate] = useState('');
    const [myPeriodsHistory, setMyPeriodsHistory] = useState([]);
    const [coordinatorHistory, setCoordinatorHistory] = useState([]);
    const [hodHistory, setHodHistory] = useState([]);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        if (!userData.email || (userData.role !== "faculty" && userData.role !== "admin")) {
            navigate("/");
            return;
        }
        setUser(userData);
        fetchFacultyDetails(userData.email, userData.role);
    }, [navigate]);

    const fetchFacultyDetails = async (email, role) => {
        try {
            const res = await fetch(`${API_URL}/faculty/profile?email=${email}&role=${role}`);
            const data = await res.json();
            setFacultyData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.email) {
            if (activeTab === 'my_periods') {
                if (subTab === 'pending') fetchMyPeriods();
                else fetchMyPeriodsHistory();
            }
            if (activeTab === 'coordinator') {
                if (subTab === 'pending') fetchCoordinatorRequests();
                else fetchCoordinatorHistory();
            }
            if (activeTab === 'hod') {
                if (subTab === 'pending') fetchHodRequests();
                else fetchHodHistory();
            }
        }
    }, [activeTab, subTab, historyDate, user]);

    const fetchMyPeriods = async () => {
        try {
            const res = await fetch(`${API_URL}/attendance/faculty/${user.email}`);
            const data = await res.json();
            if (Array.isArray(data)) {
                // Sort ascending by roll number
                const sorted = data.sort((a, b) => {
                    const ra = (a.student?.rollNo || '').toString().toLowerCase();
                    const rb = (b.student?.rollNo || '').toString().toLowerCase();
                    return ra.localeCompare(rb);
                });
                setMyPeriods(sorted);
            } else setMyPeriods([]);
        } catch (err) { console.error('Failed fetching periods', err); }
    };

    const fetchMyPeriodsHistory = async () => {
        try {
            const url = historyDate ? `${API_URL}/attendance/history/faculty/${user.email}?date=${historyDate}` : `${API_URL}/attendance/history/faculty/${user.email}`;
            const res = await fetch(url);
            const data = await res.json();
            if (Array.isArray(data)) setMyPeriodsHistory(data);
            else setMyPeriodsHistory([]);
        } catch (err) { console.error('Failed fetching periods history', err); }
    };

    const fetchCoordinatorRequests = async () => {
        try {
            const res = await fetch(`${API_URL}/attendance/coordinator/${user.email}`);
            const data = await res.json();
            if (Array.isArray(data)) setCoordinatorRequests(data);
            else setCoordinatorRequests([]);
        } catch (err) { console.error('Failed fetching coordinator requests', err); }
    };

    const fetchCoordinatorHistory = async () => {
        try {
            const url = historyDate ? `${API_URL}/attendance/history/coordinator/${user.email}?date=${historyDate}` : `${API_URL}/attendance/history/coordinator/${user.email}`;
            const res = await fetch(url);
            const data = await res.json();
            if (Array.isArray(data)) setCoordinatorHistory(data);
            else setCoordinatorHistory([]);
        } catch (err) { console.error('Failed fetching coordinator history', err); }
    };

    const fetchHodRequests = async () => {
        try {
            const res = await fetch(`${API_URL}/attendance/hod/${user.email}`);
            const data = await res.json();
            if (Array.isArray(data)) setHodRequests(data);
            else setHodRequests([]);
        } catch (err) { console.error('Failed fetching hod requests', err); }
    };

    const fetchHodHistory = async () => {
        try {
            const url = historyDate ? `${API_URL}/attendance/history/hod/${user.email}?date=${historyDate}` : `${API_URL}/attendance/history/hod/${user.email}`;
            const res = await fetch(url);
            const data = await res.json();
            if (Array.isArray(data)) setHodHistory(data);
            else setHodHistory([]);
        } catch (err) { console.error('Failed fetching hod history', err); }
    };

    const giveAttendance = async (approvalId) => {
        try {
            const res = await fetch(`${API_URL}/attendance/faculty/give-attendance`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ approval_id: approvalId })
            });
            if (res.ok) {
                fetchMyPeriods();
                setSelectedIds(prev => prev.filter(id => id !== approvalId));
            } else alert("Failed to log attendance");
        } catch (err) { console.error(err); }
    };

    const giveBulkAttendance = async () => {
        if (selectedIds.length === 0) return alert("Select at least one student first.");
        try {
            const res = await fetch(`${API_URL}/attendance/faculty/give-attendance/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ approval_ids: selectedIds })
            });
            if (res.ok) {
                alert(`Attendance confirmed for ${selectedIds.length} student(s)!`);
                setSelectedIds([]);
                fetchMyPeriods();
            } else alert("Bulk approval failed");
        } catch (err) { console.error(err); }
    };

    const handleCoordinatorAction = async (requestId, action) => {
        try {
            const res = await fetch(`${API_URL}/attendance/coordinator/approve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ request_id: requestId, action, role_email: user.email })
            });
            if (res.ok) {
                fetchCoordinatorRequests();
            } else {
                const err = await res.json();
                alert(err.error || "Action failed — are you set as coordinator for this class?");
            }
        } catch (err) { console.error(err); }
    };

    const handleHodAction = async (requestId, action) => {
        try {
            const res = await fetch(`${API_URL}/attendance/hod/approve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ request_id: requestId, action, role_email: user.email })
            });
            if (res.ok) fetchHodRequests();
            else alert("Failed action. Algorithm may have encountered an issue.");
        } catch (err) { console.error(err); }
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const selectAll = () => {
        if (selectedIds.length === myPeriods.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(myPeriods.map(ap => ap.approval_id));
        }
    };

    if (loading) return <div style={{ color: 'white', textAlign: 'center', marginTop: '5rem' }}>Loading Workflow...</div>;

    const isCoordinator = facultyData?.is_coordinator || user?.is_coordinator || false;
    const isHod = facultyData?.is_hod || user?.is_hod || false;
    const allSelected = myPeriods.length > 0 && selectedIds.length === myPeriods.length;

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <header style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/faculty/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
                        <ChevronLeft size={20} /> Back
                    </button>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Attendance Workflow</h1>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Manage student requests and class logs.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
                    </div>
                </header>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', overflowX: 'auto', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => { setActiveTab('my_periods'); setSubTab('pending'); setHistoryDate(''); }}
                        style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === 'my_periods' ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === 'my_periods' ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'relative' }}
                    >
                        My Class Periods
                        {myPeriods.length > 0 && (
                            <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                {myPeriods.length}
                            </span>
                        )}
                    </button>
                    {isCoordinator && (
                        <button
                            onClick={() => { setActiveTab('coordinator'); setSubTab('pending'); setHistoryDate(''); }}
                            style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === 'coordinator' ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === 'coordinator' ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'relative' }}
                        >
                            Coordinator Requests
                            {coordinatorRequests.length > 0 && (
                                <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                    {coordinatorRequests.length}
                                </span>
                            )}
                        </button>
                    )}
                    {isHod && (
                        <button
                            onClick={() => { setActiveTab('hod'); setSubTab('pending'); setHistoryDate(''); }}
                            style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === 'hod' ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === 'hod' ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'relative' }}
                        >
                            HOD Approvals
                            {hodRequests.length > 0 && (
                                <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                    {hodRequests.length}
                                </span>
                            )}
                        </button>
                    )}
                    <button
                        onClick={() => navigate('/faculty/scanner-attendance')}
                        style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}
                    >
                        <Camera size={16} /> Take Attendance
                    </button>
                </div>

                {/* Sub-Tabs for Pending / History */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={() => setSubTab('pending')}
                            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: subTab === 'pending' ? 'var(--accent-color)' : 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            Pending Requirements
                        </button>
                        <button
                            onClick={() => setSubTab('history')}
                            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: subTab === 'history' ? 'var(--accent-color)' : 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            Previous Approvals
                        </button>
                    </div>

                    {subTab === 'history' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.05)', padding: '0.5rem 1rem', borderRadius: '8px' }}>
                            <Calendar size={18} color="var(--accent-color)" />
                            <input
                                type="date"
                                value={historyDate}
                                onChange={(e) => setHistoryDate(e.target.value)}
                                style={{ background: 'transparent', border: 'none', color: 'white', outline: 'none', fontFamily: 'inherit' }}
                            />
                            {historyDate && (
                                <button onClick={() => setHistoryDate('')} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>Clear</button>
                            )}
                        </div>
                    )}
                </div>

                {/* ===================== MY PERIODS TAB — ACCORDION ===================== */}
                {activeTab === 'my_periods' && subTab === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {myPeriods.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No pending period attendances routed to you.
                            </div>
                        ) : (
                            <>
                                {/* Bulk toolbar */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 1.2rem', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', marginBottom: '0.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={selectAll}
                                            style={{ width: '16px', height: '16px', accentColor: 'var(--accent-color)', cursor: 'pointer' }}
                                        />
                                        Select All ({myPeriods.length} students)
                                    </label>
                                    <button
                                        onClick={giveBulkAttendance}
                                        disabled={selectedIds.length === 0}
                                        style={{
                                            background: selectedIds.length > 0 ? '#10b981' : 'rgba(255,255,255,0.05)',
                                            color: selectedIds.length > 0 ? 'white' : 'var(--text-muted)',
                                            border: 'none', padding: '0.6rem 1.4rem', borderRadius: '8px',
                                            cursor: selectedIds.length > 0 ? 'pointer' : 'not-allowed',
                                            fontWeight: 'bold', fontSize: '0.9rem',
                                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <CheckCircle size={16} />
                                        Bulk Accept {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                                    </button>
                                </div>

                                {/* Accordion list */}
                                {myPeriods.map((ap) => {
                                    const isExpanded = !!expandedRows[ap.approval_id];
                                    const isSelected = selectedIds.includes(ap.approval_id);

                                    return (
                                        <div
                                            key={ap.approval_id}
                                            className="glass-card"
                                            style={{
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                border: isSelected ? '1px solid var(--accent-color)' : '1px solid var(--glass-border)',
                                                transition: 'border 0.2s'
                                            }}
                                        >
                                            {/* Summary row — always visible */}
                                            <div style={{ display: 'flex', alignItems: 'center', padding: '1rem 1.2rem', gap: '1rem' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(ap.approval_id)}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ width: '16px', height: '16px', accentColor: 'var(--accent-color)', cursor: 'pointer', flexShrink: 0 }}
                                                />

                                                {/* Roll No */}
                                                <div style={{ width: '130px', fontWeight: 'bold', color: 'var(--accent-color)', fontSize: '0.9rem', flexShrink: 0 }}>
                                                    {ap.student?.rollNo || '—'}
                                                </div>

                                                {/* Name */}
                                                <div
                                                    style={{ flex: 1, fontWeight: '600', color: 'var(--text-primary)', cursor: 'pointer' }}
                                                    onClick={() => toggleRow(ap.approval_id)}
                                                >
                                                    {ap.student?.name || ap.student_email}
                                                </div>

                                                {/* Period badge */}
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
                                                    {ap.period_number ? `P${ap.period_number}` : ap.start_time || 'Full Day'}
                                                </div>

                                                {/* Individual accept */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); giveAttendance(ap.approval_id); }}
                                                    style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.45rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                                                >
                                                    <CheckCircle size={14} /> Accept
                                                </button>

                                                {/* Expand toggle */}
                                                <div onClick={() => toggleRow(ap.approval_id)} style={{ cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0 }}>
                                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                </div>
                                            </div>

                                            {/* Expanded detail panel */}
                                            {isExpanded && (
                                                <div style={{ padding: '0 1.2rem 1.2rem 1.2rem', borderTop: '1px solid var(--glass-border)', background: 'rgba(0,0,0,0.1)' }}>
                                                    <div style={{ paddingTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                                        <div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem', color: 'var(--text-secondary)' }}>
                                                                <Calendar size={14} /> {ap.date}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period / Time</div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem', color: 'var(--text-secondary)' }}>
                                                                <Clock size={14} />
                                                                {ap.period_number ? `Period ${ap.period_number}` : ap.start_time ? `Time: ${ap.start_time}` : 'Full Day'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Year / Dept / Section</div>
                                                            <div style={{ marginTop: '0.2rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                                {ap.student?.year} — {ap.student?.department} — Sec {ap.student?.section}
                                                            </div>
                                                        </div>
                                                        {ap.attendance_percentage != null && (
                                                            <div>
                                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reported Attendance %</div>
                                                                <div style={{ marginTop: '0.2rem', fontWeight: 'bold', color: ap.attendance_percentage < 75 ? '#ef4444' : '#10b981', fontSize: '1.1rem' }}>
                                                                    {ap.attendance_percentage}%
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{ gridColumn: '1 / -1' }}>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reason</div>
                                                            <div style={{ marginTop: '0.3rem', color: 'var(--text-primary)', background: 'rgba(255,255,255,0.03)', padding: '0.6rem 0.8rem', borderRadius: '6px', fontSize: '0.9rem' }}>
                                                                {ap.reason}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
                {activeTab === 'my_periods' && subTab === 'history' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {myPeriodsHistory.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No previous approvals found for {historyDate || 'all dates'}.
                            </div>
                        ) : myPeriodsHistory.map(ap => (
                            <div key={ap.approval_id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', borderLeft: '4px solid #10b981' }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {ap.student?.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 'normal' }}>({ap.student?.rollNo})</span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {ap.date} &nbsp;|&nbsp; <strong>Period / Time:</strong> {ap.period_number ? `Period ${ap.period_number}` : ap.start_time ? `Time: ${ap.start_time}` : 'Full Day'}
                                    </div>
                                    <div style={{ marginTop: '0.4rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                        Reason: {ap.reason}
                                    </div>
                                </div>
                                <div style={{ color: '#10b981', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <CheckCircle size={18} /> Attendance Given
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ===================== COORDINATOR TAB ===================== */}
                {activeTab === 'coordinator' && subTab === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ padding: '0.8rem 1rem', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--accent-color)' }}>
                            📋 As Class Coordinator, review requests from your students and forward approved ones to the HOD.
                        </div>
                        {coordinatorRequests.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No pending requests for your class.
                            </div>
                        ) : coordinatorRequests.map(req => (
                            <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {req.student?.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 'normal' }}>({req.student?.rollNo})</span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {req.date} &nbsp;|&nbsp; <strong>Type:</strong> {req.type} &nbsp;|&nbsp;
                                        <strong> Periods:</strong> {req.type === 'full_day' ? 'All Day' : (req.periods ? req.periods.join(', ') : 'N/A')}
                                    </div>
                                    {req.attendance_percentage != null && (
                                        <div style={{ marginTop: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: req.attendance_percentage < 75 ? '#ef4444' : '#10b981' }}>
                                            Attendance: {req.attendance_percentage}%
                                        </div>
                                    )}
                                    <div style={{ marginTop: '0.4rem', color: 'var(--accent-color)', fontSize: '0.9rem' }}>
                                        Reason: {req.reason}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                    <button
                                        onClick={() => handleCoordinatorAction(req.id, 'approve')}
                                        style={{ background: 'var(--accent-color)', color: 'white', border: 'none', padding: '0.8rem 1.4rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                                    >
                                        ➜ Forward to HOD
                                    </button>
                                    <button
                                        onClick={() => handleCoordinatorAction(req.id, 'reject')}
                                        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'none', padding: '0.8rem 1.2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'coordinator' && subTab === 'history' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {coordinatorHistory.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No previous approvals found for {historyDate || 'all dates'}.
                            </div>
                        ) : coordinatorHistory.map(req => (
                            <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', borderLeft: '4px solid #10b981' }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {req.student?.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 'normal' }}>({req.student?.rollNo})</span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {req.date} &nbsp;|&nbsp; <strong>Type:</strong> {req.type}
                                    </div>
                                    <div style={{ marginTop: '0.4rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                        Reason: {req.reason}
                                    </div>
                                </div>
                                <div style={{ color: '#10b981', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <CheckCircle size={18} /> Forwarded to HOD
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ===================== HOD TAB ===================== */}
                {activeTab === 'hod' && subTab === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {hodRequests.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No coordinator-approved requests pending your review.
                            </div>
                        ) : hodRequests.map(req => (
                            <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {req.student?.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 'normal' }}>({req.student?.year} - {req.student?.section})</span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {req.date} | <strong>Type:</strong> {req.type} |
                                        <strong> Periods:</strong> {req.type === 'full_day' ? 'All' : (req.periods ? req.periods.join(', ') : 'N/A')}
                                    </div>
                                    {req.attendance_percentage != null && (
                                        <div style={{ marginTop: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: req.attendance_percentage < 75 ? '#ef4444' : '#10b981' }}>
                                            Reported Attendance: {req.attendance_percentage}%
                                        </div>
                                    )}
                                    <div style={{ marginTop: '0.5rem', color: 'var(--accent-color)', fontSize: '0.9rem' }}>
                                        Reason: {req.reason}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                    <button
                                        title="Approve and Route to Faculty"
                                        onClick={() => handleHodAction(req.id, 'approve')}
                                        style={{ background: 'var(--accent-color)', color: 'white', border: 'none', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >Final Approve</button>
                                    <button
                                        onClick={() => handleHodAction(req.id, 'reject')}
                                        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'none', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'hod' && subTab === 'history' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {hodHistory.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No previous approvals found for {historyDate || 'all dates'}.
                            </div>
                        ) : hodHistory.map(req => (
                            <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', borderLeft: '4px solid #10b981' }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {req.student?.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 'normal' }}>({req.student?.year} - {req.student?.section})</span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        <strong>Date:</strong> {req.date} &nbsp;|&nbsp; <strong>Type:</strong> {req.type}
                                    </div>
                                    <div style={{ marginTop: '0.4rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                        Reason: {req.reason}
                                    </div>
                                </div>
                                <div style={{ color: '#10b981', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <CheckCircle size={18} /> Final Approved
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FacultyAttendance;
